
import type { EffectArchetype, MeshEffectKind } from "./types";
import type { MeshEffectDriver } from "./MeshEffectDriver";

/**
 * MeshEffect — expresses intent ("I am wind") without knowing implementation details.
 *
 * Each mesh effect:
 *   - Declares which archetypes it supports
 *   - Resolves the right driver for a given archetype
 *   - Provides default parameters when asset metadata has none
 *
 * Mesh effects are stateless singletons. The MeshEffectManager holds the registry.
 *
 * Note: MeshEffects modify existing meshes via shader injection (onBeforeCompile).
 * They are distinct from ObjectEffects (additive scene-graph children like emitters/lights).
 */
export abstract class MeshEffect<P> {
  abstract readonly kind: MeshEffectKind;
  abstract readonly supportedArchetypes: EffectArchetype[];

  /** Resolve the driver for a given archetype, or null if unsupported. */
  abstract resolveDriver(
    archetype: EffectArchetype,
  ): MeshEffectDriver<P> | null;

  /** Default parameters when the asset has no metadata.effects entry. */
  abstract getDefaultParams(archetype: EffectArchetype): P;
}
